import React, { useEffect, useMemo, useState } from "react";
import ContentArea from "../ContentArea/ContentArea";
import Layout from "./Layout/Layout";
import StatusMessage from "../StatusMessage/StatusMessage";
import LineChart, { LineChartDatum, LineChartMarker, LineChartTick } from "../LineChart/LineChart";
import StatusDataLoader from "../../services/StatusDataLoader";
import BlockCalculator from "../../utils/Calculators/InformationCalculator";
import {
  buildWeeklyEmissionSeries,
  getGenesisDate,
  WeeklyEmissionPoint,
} from "../../utils/Calculators/EmissionCalculator";
import { useSeo } from "../../utils/Hooks/useSeo";
import "./EmissionPage.css";

const END_YEAR = 2140;
const WEEKS_PER_YEAR = 52;
const SEO_DESCRIPTION =
  "Track total Bitcoin emitted, current block subsidy, and projected issuance trajectory through 2140.";

const formatBtc = (value: number, decimals = 2) => {
  const factor = 10 ** decimals;
  const floored = Math.floor(value * factor) / factor;
  return `${floored.toLocaleString("en-US", { maximumFractionDigits: decimals })} BTC`;
};

const formatCompactBtc = (value: number) => {
  const floored = Math.floor(value);
  return `${floored.toLocaleString("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  })} BTC`;
};

const formatDate = (date: Date) =>
  new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);

const buildTwentyYearTicks = (start: Date, end: Date): LineChartTick[] => {
  const startYear = start.getUTCFullYear();
  const endYear = end.getUTCFullYear();
  const ticks: LineChartTick[] = [];

  const firstTickYear = Math.ceil(startYear / 20) * 20;
  for (let year = firstTickYear; year <= endYear; year += 20) {
    const tickDate = new Date(Date.UTC(year, 0, 1));
    if (tickDate.getTime() < start.getTime() || tickDate.getTime() > end.getTime()) {
      continue;
    }
    ticks.push({ date: tickDate, label: `${year}` });
  }

  return ticks;
};

export const EmissionPage = () => {
  const [loading, setLoading] = useState(true);
  const [currentBlock, setCurrentBlock] = useState(0);

  useSeo({
    title: "Bitcoin Emission",
    description: SEO_DESCRIPTION,
    canonicalPath: "/emission",
  });

  useEffect(() => {
    const load = async () => {
      const status = await StatusDataLoader.getStatusData();
      const blockHeight = status?.network?.currentBlock ?? 0;
      setCurrentBlock(blockHeight);
      setLoading(false);
    };

    load();
  }, []);

  const { chartData, markers, ticks } = useMemo(() => {
    const today = new Date();
    const endDate = new Date(Date.UTC(END_YEAR, 11, 31));

    const series: WeeklyEmissionPoint[] = buildWeeklyEmissionSeries(endDate);
    const chartData: LineChartDatum[] = series.map((point) => ({
      date: point.date,
      value: point.totalEmissionBtc,
    }));

    const markers: LineChartMarker[] = [
      { date: today, label: "Today", tone: "accent" },
    ];

    const ticks = buildTwentyYearTicks(getGenesisDate(), endDate);

    return { chartData, markers, ticks };
  }, []);

  const rewardPerBlock = BlockCalculator.getCurrentBlockReward(currentBlock);
  const currentEmission = BlockCalculator.calculateCurrentEmission(currentBlock);
  const weeklyEmission = rewardPerBlock * 144 * 7;
  const dailyEmission = weeklyEmission / 7;
  const yearlyEmission = weeklyEmission * WEEKS_PER_YEAR;

  const renderContent = () => {
    if (loading) {
      return (
        <div className="progress-circle">
          <progress className="pure-material-progress-circular" />
        </div>
      );
    }

    if (!chartData.length) {
      return (
        <StatusMessage
          title="We could not calculate emission history."
          description="Please try again in a minute."
          minHeightRem={24}
        />
      );
    }

    return (
      <div className="content-lines">
        <section className="emission-overview-card">
          <div className="emission-overview-top">
            <div>
              <h1 className="emission-overview-title">Bitcoin Emission</h1>
              <div className="emission-overview-subtitle">{SEO_DESCRIPTION}</div>
            </div>
            <div className="emission-current-panel">
              <div className="emission-current-label">Total emitted to date</div>
              <div className="emission-current-value">{formatBtc(currentEmission, 0)}</div>
            </div>
          </div>

          <div className="emission-metrics-grid">
            <div className="emission-metric">
              <div className="emission-metric-label">Block reward</div>
              <div className="emission-metric-value">{formatBtc(rewardPerBlock, 3)}</div>
            </div>
            <div className="emission-metric">
              <div className="emission-metric-label">Daily issuance</div>
              <div className="emission-metric-value">{formatBtc(dailyEmission, 2)}</div>
            </div>
            <div className="emission-metric">
              <div className="emission-metric-label">Weekly issuance</div>
              <div className="emission-metric-value">{formatBtc(weeklyEmission, 2)}</div>
            </div>
            <div className="emission-metric">
              <div className="emission-metric-label">Yearly issuance</div>
              <div className="emission-metric-value">{formatBtc(yearlyEmission, 2)}</div>
            </div>
          </div>
        </section>

        <div className="emission-chart-wrap">
          <LineChart
            data={chartData}
            widthRem={60}
            heightRem={24}
            title="Bitcoin Total Emission"
            xAxisLabel="Date"
            yAxisLabel="Total BTC emitted"
            valueFormatter={(value) => formatBtc(value, 0)}
            yAxisValueFormatter={formatCompactBtc}
            dateFormatter={formatDate}
            markers={markers}
            xTicks={ticks}
            yPaddingFraction={0.04}
          />
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <ContentArea isTopArea>
        {renderContent()}
      </ContentArea>
    </Layout>
  );
};
